<template>
  <div class="colors">
    <Hero
      class="hero"
      heading="Colors & Shades"
    />
    <PageContainer>
      <div class="color-container">
        <div class="primary">
          <p>ST Orange</p>
          <p>#FF5500</p>
          <p>CMYK: 0,80,100,0</p>
          <p>NCS: S 0585-Y70R</p>
        </div>
        <div class="secondary">
          <p>ST Light Orange</p>
          <p>#FFECE5</p>
          <p>CMYK: 0,14,14,0</p>
          <p>NCS: S 0510-Y40R </p>
        </div>
        <div class="white">
          <p>ST White</p>
          <p>#FFFFFF</p>
          <div class="complementary-colors-container">
            <div class="black-container">
              <div class="black">
                <p>ST Black</p>
                <p>#000000</p>
              </div>
              <div class="black80">
                <p>80%</p>
                <p>#333333</p>
              </div>
              <div class="black60">
                <p>60%</p>
                <p>#666666</p>
              </div>
              <div class="black30">
                <p>30%</p>
                <p>#B2B2B2</p>
              </div>
            </div>
            <div class="blue-container">
              <div class="blue">
                <p>ST Indigo</p>
                <p>#2D3C64</p>
              </div>
              <div class="blue80">
                <p>80%</p>
                <p>#576383</p>
              </div>
              <div class="blue60">
                <p>60%</p>
                <p>#818AA2</p>
              </div>
              <div class="blue30">
                <p>30%</p>
                <p>#C0C4D0</p>
              </div>
            </div>
            <div class="green-container">
              <div class="green">
                <p>ST Forest</p>
                <p>#365A4D</p>
              </div>
              <div class="green80">
                <p>80%</p>
                <p>#5E7B71</p>
              </div>
              <div class="green60">
                <p>60%</p>
                <p>#869C94</p>
              </div>
              <div class="green30">
                <p>30%</p>
                <p>#C3CDCA</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TextBlock title="The orange is meant to be seen">
        <p>
          Orange combines the energy of red and the happiness of yellow.
          Orange represents enthusiasm, fascination, happiness, creativity,
          determination, attraction, success, encouragement, and stimulation.
        </p>
    </TextBlock>
    <div class="explanation-container">
      <ColorExplanation
        :mainColor="'orange'"
        :secondaryColor="'light-orange'"
        title="Great ROI, campaign after campaign"
        body="Orange is our main brand color. Always use this in presentations
        and applications when talking about SeenThis as a company or brand. Use
        it as a fall-back when you feel a bit design-confused."
      />
      <ColorExplanation
        :mainColor="'blue'"
        :secondaryColor="'light-blue'"
        title="Never compromise on quality"
        body="The ST Indigo is a color we use when we talk more in-depth about
        our products and the great benefits of using them. Faster loading, better
        reults, crisp hi-def, faster internet etc. etc."
      />
      <ColorExplanation
        :mainColor="'green'"
        :secondaryColor="'light-green'"
        title="The time to act is now"
        body="The ST Forest is a complmentary color to be used when we talk
        about the positive effects of less data. It's not a green labeling
        for all things concerning climate but more of a supportive visual
        element to highlight this kind of messaging. "
      />
    </div>
    <div class="delimiter"></div>
    <TextBlock title="Using the Colors">
      <p>
        Color shading can be tricky. Know that it's always ok to use too
        much white, but never too much black. We are a friendly, open company
        and if you go too dark, it's easy to be perceived as goth. Black and
        white is boring, but orange and white makes no one uncomfortable. So
        when in doubt - go with white and a splash of orange.
      </p>
      <p>
        Below, you'll find some good examples and do's and don'ts.
      </p>
    </TextBlock>
    <div class="tile-container">
      <p class="primary-secondary">Great example of usage</p>
      <p class="white-primary">Good example of usage</p>
      <p class="black-white">OK example of usage</p>
      <p class="secondary-black">OK example of usage</p>
      <p class="black-green30">Good example of usage</p>
      <p class="white-green">Good example of usage</p>
      <p class="green-green30">OK example of usage</p>
      <p class="primary-blue30">OK example of usage</p>
      <p class="secondary-blue">OK example of usage</p>
      <p class="primary-black">OK example of usage</p>
      <p class="primary-black80">OK example of usage</p>
      <p class="primary-black60">Bad example of usage</p>
      <p class="primary-green">Bad example of usage</p>
      <p class="green-blue">Bad example of usage</p>
    </div>
    <ViLinks />
    </PageContainer>
  </div>
</template>

<script>
import Hero from '../components/hero.vue';
import PageContainer from '../components/page-container.vue';
import TextBlock from '../components/text-block.vue';
import ColorExplanation from '../components/color-explanation.vue';
import ViLinks from '../components/visual-id-links.vue';

export default {
  name: 'Colors',
  components: {
    Hero,
    TextBlock,
    PageContainer,
    ColorExplanation,
    ViLinks,
  },
};
</script>


<style lang="stylus" scoped>
  @require '../style/colors'

  // COLOR-BLOCKS
  .color-container
    display: flex
    flex-wrap: wrap
    justify-content: center
    margin-top: 7rem
    margin-bottom: 10rem
    font-family: space-mono
    font-size: 1.8rem
    line-height: 2rem

    >*
      box-sizing: border-box
      height: 55rem
      padding: 2rem

    p
      margin: 0
      margin-bottom: 0.3rem

    .primary
      width: 37rem
      background-color: $primary-color

    .secondary
      width: 23rem
      background-color: $secondary-color

    .white
      position: relative
      width: 50rem
      border: 1px solid #B2B2B2
      border-left: none
      border-bottom: none
      background-color: $white

      .complementary-colors-container
        display: flex
        flex-direction: column
        position: absolute
        bottom: 0
        margin: 0 -2rem
        width: 100%
        font-size: 1.4rem
        line-height: 1.6rem
        color: $white

        >*
          display: flex
          height: 8rem

          >*
            width: 22%
            padding: 0.5rem

          >*:first-child
            width: 34%
            padding: 1rem 2rem

    @media only screen and (max-width: 1200px)
      >*
        height: 40rem
      .white
        width: 60rem
        border: 1px solid #B2B2B2
        border-top: none
        border-bottom: none

    @media only screen and (max-width: 650px)
      .primary,
      .secondary,
      .white
        width: 100%
        height: 25rem

      .white
        margin-bottom: 24rem
        border: none

        .complementary-colors-container
          bottom: -24rem

  .black,
  .blue,
  .green
    font-size: 1.8rem
    line-height: 2rem
  .black
    background-color: $black

  .black80
    background-color: $black80
  .black60
    background-color: $black60
  .black30
    background-color: $black30

  .blue
    background-color: $blue

  .blue80
    background-color: $blue80

  .blue60
    background-color: $blue60

  .blue30
    background-color: $blue30

  .green
    background-color: $green

  .green80
    background-color: $green80

  .green60
    background-color: $green60

  .green30
    background-color: $green30
  //END COLOR-BLOCKS

  .explanation-container
    display: flex
    flex-wrap: wrap
    justify-content: center
    max-width: 100rem
    margin: 0 auto
    gap: 6rem 3rem

  .delimiter
    height: 2px
    width: 75%
    margin: 8rem auto
    background-color: $black

  .tile-container
    display: flex
    flex-wrap: wrap
    justify-content: center
    gap: 1rem
    max-width: 80rem
    margin: 0 auto 8rem auto
    font-size: 3rem
    font-weight: 700
    line-height: 3.2rem

    >*
      width: 20rem
      height: 12rem
      margin: 0
      padding: 2rem 2rem 2rem 3rem

    .primary-secondary
      width: 72rem
      height: 21rem
      font-size: 7rem
      line-height: 7rem
      color: $primary-color
      background-color: $secondary-color

    .white-primary
      color: $white
      background-color: $primary-color

    .black-white
      color: $black
      background-color: $white
      border: 1px solid #B2B2B2

    .secondary-black
      color: $secondary-color
      background-color: $black

    .black-green30
      width: 46rem
      color: $black
      background-color: $green30

    .white-green
      color: $white
      background-color: $green

    .green-green30
      color: $green
      background-color: $green30

    .primary-blue30
      color: $primary-color
      background-color: $blue30

    .secondary-blue
      color: $secondary-color
      background-color: $blue

    .primary-black
      color: $primary-color
      background-color: $black

    .primary-black80
      color: $primary-color
      background-color: $black80

    .primary-black60
      color: $primary-color
      background-color: $black60

    .primary-green
      width: 33rem
      color: $primary-color
      background-color: $green

    .green-blue
      width: 33rem
      color: $green
      background-color: $blue

    @media only screen and (max-width: 800px)

      >*,
      .primary-secondary,
      .black-green30,
      .primary-green,
      .green-blue
        width: 40%

      .primary-secondary
        height: 12rem
        font-size: inherit
        line-height: inherit

    @media only screen and (max-width: 600px)
      >*,
      .primary-secondary,
      .black-green30,
      .primary-green,
      .green-blue
        width: 100%
        display: flex
        justify-content: center
        align-items: center

</style>
